import { Alert, Stack, Typography } from '@mui/material';

import Dialog from 'modules/common/components/MUI/Dialog';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

interface IncompleteStatementsDialogProps {
    readonly open: boolean;
    /**
     * Dismiss and go back to supplying statements. Nothing is written.
     *
     * Also the handler for the X and the backdrop: every way out of this dialog that isn't
     * "Submit anyway" has to leave the member exactly where they were.
     */
    readonly onAddMore: () => void;
    /** Proceed with the months still outstanding. */
    readonly onSubmitAnyway: () => void;
    /**
     * The account as the card names it, passed in rather than composed here — this dialog is
     * opened over a card or a modal the member is looking at, and a second spelling of the same
     * account reads as a different one.
     *
     * Omitted when the prompt covers more than one account, which the step-completion route can:
     * naming one of several would be worse than naming none, since the member would fix that one
     * and meet the same dialog again.
     */
    readonly accountLabel?: string;
    /**
     * The months still outstanding for this account.
     *
     * The caller's current reading of missing periods. The upload modal passes persisted coverage,
     * because a staged assignment may still be discarded when the member closes it.
     */
    readonly missingPeriods: StatementPeriod[];
}

/**
 * Confirms that a member means to leave months outstanding on one account.
 *
 * Serves both ways a member can do that: ticking "I don't have these right now" on a card, and
 * leaving the upload modal with months still unfilled. One dialog for both because the decision is
 * identical — proceed with an incomplete account — and the cost lands later on bookkeeping either
 * way. Naming the count and the months makes it a decision about real data rather than a box to
 * clear on the way to Continue.
 *
 * The emphasis is deliberately inverted from a normal confirmation: **Add more** is the contained
 * button and **Submit anyway** is a text button. The safe action is the one being encouraged, and
 * proceeding is the one that costs something.
 */
export default function IncompleteStatementsDialog({
    open,
    onAddMore,
    onSubmitAnyway,
    accountLabel = undefined,
    missingPeriods,
}: Readonly<IncompleteStatementsDialogProps>) {
    const count = missingPeriods.length;
    /**
     * Counted where there is a count to state, generic where there isn't — and named only when
     * one account is in question, since the multi-account route has no single name to give.
     */
    const statementNoun = count === 1 ? 'statement' : 'statements';
    const scope = accountLabel ? ` ${accountLabel}` : '';
    const headingLabel = count
        ? `You're missing ${count} ${statementNoun}`
        : `You're missing documents for this account`;
    const missingLabel = count
        ? `We need ${count} more${scope} ${statementNoun} to process your books.`
        : `We still need statements for${scope || ' this account'}.`;

    return (
        <Dialog
            open={open}
            onClose={onAddMore}
            heading={headingLabel}
            size="medium"
            negativeAction={{ label: 'Submit anyway', variant: 'text', onClick: onSubmitAnyway }}
            positiveAction={{ label: 'Upload remaining', onClick: onAddMore }}>
            <Stack spacing={2}>
                <Alert severity="warning">
                    Submitting now may delay your bookkeeping, and you might need to upload them later.
                </Alert>
                <Typography variant="body1">{missingLabel}</Typography>
            </Stack>
        </Dialog>
    );
}
